import React, { useState, useRef } from "react";

import {
  Cancel,
  ImageTypeValidation,
  MaxFileSizeError,
  ProfilePhotoInstruction,
  Save,
  UploadProfilePhoto,
} from "../../utils/Constant";
import { MAX_FILE_SIZE_IN_BYTES_2MB } from "../../utils/helper/helper";
import H2 from "../Headings/H2Element";
import MUIIcons from "../MUIIcon/MUIIcons";

import { FileDropZone } from "./FileDropZone";
import { ImagePreview } from "./ImagePreview";

import "./ImageUploadModal.scss";

interface ImageUploadModalProps {
  onClose: () => void;
  onImageSelect: (file: File) => void;
  currentImage?: string;
  handleImageUpload?: any;
}

export const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  onClose,
  onImageSelect,
  handleImageUpload,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [error, setError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): boolean => {
    setError("");

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      setError(ImageTypeValidation);
      return false;
    }

    if (file.size > MAX_FILE_SIZE_IN_BYTES_2MB) {
      setError(MaxFileSizeError);
      return false;
    }

    return true;
  };

  const handleFileSelect = (file: File) => {
    if (validateFile(file)) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      onImageSelect(file);
    }
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleSave = () => {
    if (selectedFile) {
      handleImageUpload();
    }
  };

  const handleCancel = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    onClose();
  };

  const handleDropZoneClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="image-upload-modal-overlay" onClick={handleCancel}>
      <div className="image-upload-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <H2>{UploadProfilePhoto}</H2>
          <button className="close-button" onClick={handleCancel}>
            <MUIIcons
              iconName="CloseOutlined"
              className="primary-icon-color"
              size={24}
            />
          </button>
        </div>

        <div className="modal-body">
          <div className="upload-content">
            {!selectedFile ? (
              <div onClick={handleDropZoneClick}>
                <FileDropZone onFileSelect={handleFileSelect} />
              </div>
            ) : (
              <ImagePreview
                imageUrl={previewUrl}
                fileName={selectedFile.name}
              />
            )}
          </div>

          {error && (
            <div className="error-message">
              <MUIIcons
                iconName="ErrorOutlineOutlined"
                className="primary-icon-color"
                size={24}
              />
              <span>{error}</span>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png"
            onChange={handleFileInputChange}
            style={{ display: "none" }}
          />

          <pre className="file-requirements">{ProfilePhotoInstruction()}</pre>
        </div>

        <div className="modal-footer">
          <button className="cancel-button" onClick={handleCancel}>
            {Cancel}
          </button>
          <button
            className="save-button"
            onClick={handleSave}
            disabled={!selectedFile}
          >
            {Save}
          </button>
        </div>
      </div>
    </div>
  );
};
